import { createAction, handleActions } from "redux-actions";

import { createRoutine, promisifyRoutine } from "redux-saga-routines";

import { BUSINESS_ACTIVITY_KIND, FORM_DATA_KEY_BY_KIND } from "./invoiceFields";

const PRIVATE_PERSON_DATA_KEY =
  FORM_DATA_KEY_BY_KIND[BUSINESS_ACTIVITY_KIND.PRIVATE_PERSON];
const OTHER_BUSINESS_DATA_KEY =
  FORM_DATA_KEY_BY_KIND[BUSINESS_ACTIVITY_KIND.OTHER_BUSINESS];
const SELF_EMPLOYED_DATA_KEY =
  FORM_DATA_KEY_BY_KIND[BUSINESS_ACTIVITY_KIND.SELF_EMPLOYED];

export const FETCH_SAVED_INVOICE_DATA = "app/invoices/FETCH_SAVED_INVOICE_DATA";
export const fetchSavedInvoiceData = createRoutine(FETCH_SAVED_INVOICE_DATA);
export const fetchSavedInvoiceDataPromiseCreator = promisifyRoutine(
  fetchSavedInvoiceData
);

export const UPDATE_SAVED_INVOICE_DATA =
  "app/invoices/UPDATE_SAVED_INVOICE_DATA";
export const updateSavedInvoiceData = createRoutine(UPDATE_SAVED_INVOICE_DATA);
export const updateSavedInvoiceDataPromiseCreator = promisifyRoutine(
  updateSavedInvoiceData
);

export const FETCH_INVOICE_COUNTRIES = "app/invoices/FETCH_INVOICE_COUNTRIES";
export const fetchInvoiceCountries = createRoutine(FETCH_INVOICE_COUNTRIES);

export const UPDATE_INVOICE_FORM_DATA = "app/invoices/UPDATE_INVOICE_FORM_DATA";
export const updateInvoiceFormData = createAction(
  UPDATE_INVOICE_FORM_DATA,
  payload => payload
);

export const UPDATE_INVOICE_FORM_ERROR =
  "app/invoices/UPDATE_INVOICE_FORM_ERROR";
export const updateInvoiceFormError = createAction(
  UPDATE_INVOICE_FORM_ERROR,
  payload => payload
);

export const CLEAR_INVOICES = "app/invoices/CLEAR_INVOICES";
export const clearInvoices = createAction(CLEAR_INVOICES, () => {});

export const initialState = {
  businessActivityKind: null,
  savedPrivatePersonData: null,
  savedOtherBusinessData: null,
  savedSelfEmployedData: null,
  savedInvoicesEnabled: false,
  invoiceCountries: [],
  invoiceFormData: {
    businessActivityKind: null,
    invoicesEnabled: null,
    [PRIVATE_PERSON_DATA_KEY]: null,
    [OTHER_BUSINESS_DATA_KEY]: null,
    [SELF_EMPLOYED_DATA_KEY]: null
  },
  invoiceFormErrors: {
    [PRIVATE_PERSON_DATA_KEY]: null,
    [OTHER_BUSINESS_DATA_KEY]: null,
    [SELF_EMPLOYED_DATA_KEY]: null
  },
  loading: true
};

export default handleActions(
  {
    [fetchSavedInvoiceData.TRIGGER]: (state, { payload }) => {
      if (payload?.silent) {
        return state;
      }

      return {
        ...state,
        loading: true
      };
    },
    [fetchSavedInvoiceData.SUCCESS]: (state, { payload }) => {
      return {
        ...state,
        loading: false,
        businessActivityKind: payload?.businessActivityKind ?? null,
        savedPrivatePersonData: payload?.savedPrivatePersonData ?? null,
        savedOtherBusinessData: payload?.savedOtherBusinessData ?? null,
        savedSelfEmployedData: payload?.savedSelfEmployedData ?? null,
        savedInvoicesEnabled: payload?.savedInvoicesEnabled ?? false
      };
    },
    [fetchSavedInvoiceData.FAILURE]: state => {
      return {
        ...state,
        loading: false
      };
    },
    [fetchInvoiceCountries.SUCCESS]: (state, { payload }) => {
      return {
        ...state,
        invoiceCountries: payload?.countries ?? []
      };
    },
    [fetchInvoiceCountries.FAILURE]: state => {
      return state;
    },
    [UPDATE_INVOICE_FORM_DATA]: (state, { payload }) => {
      return {
        ...state,
        invoiceFormData: {
          ...state.invoiceFormData,
          ...(payload?.businessActivityKind !== undefined && {
            businessActivityKind: payload.businessActivityKind
          }),
          ...(payload?.invoicesEnabled !== undefined && {
            invoicesEnabled: payload.invoicesEnabled
          }),
          ...(payload?.[PRIVATE_PERSON_DATA_KEY] !== undefined && {
            [PRIVATE_PERSON_DATA_KEY]: payload[PRIVATE_PERSON_DATA_KEY]
          }),
          ...(payload?.[OTHER_BUSINESS_DATA_KEY] !== undefined && {
            [OTHER_BUSINESS_DATA_KEY]: payload[OTHER_BUSINESS_DATA_KEY]
          }),
          ...(payload?.[SELF_EMPLOYED_DATA_KEY] !== undefined && {
            [SELF_EMPLOYED_DATA_KEY]: payload[SELF_EMPLOYED_DATA_KEY]
          })
        }
      };
    },
    [updateSavedInvoiceData.TRIGGER]: (state, { payload }) => {
      return {
        ...state,
        ...(payload?.silent ? {} : { loading: true }),
        invoiceFormErrors: null
      };
    },
    [updateSavedInvoiceData.SUCCESS]: (state, { payload }) => {
      if (!payload) {
        return {
          ...state,
          loading: false
        };
      }

      return {
        ...state,
        loading: false,
        ...(payload.savedInvoicesEnabled !== undefined && {
          savedInvoicesEnabled: payload.savedInvoicesEnabled
        }),
        ...(payload.businessActivityKind !== undefined && {
          businessActivityKind: payload.businessActivityKind
        }),
        ...(payload.savedPrivatePersonData !== undefined && {
          savedPrivatePersonData: payload.savedPrivatePersonData
        }),
        ...(payload.savedOtherBusinessData !== undefined && {
          savedOtherBusinessData: payload.savedOtherBusinessData
        }),
        ...(payload.savedSelfEmployedData !== undefined && {
          savedSelfEmployedData: payload.savedSelfEmployedData
        })
      };
    },
    [updateSavedInvoiceData.FAILURE]: (state, { payload }) => {
      return {
        ...state,
        loading: false,
        invoiceFormErrors: {
          ...state.invoiceFormErrors,
          ...(payload?.[PRIVATE_PERSON_DATA_KEY] !== undefined && {
            [PRIVATE_PERSON_DATA_KEY]: payload[PRIVATE_PERSON_DATA_KEY]
          }),
          ...(payload?.[OTHER_BUSINESS_DATA_KEY] !== undefined && {
            [OTHER_BUSINESS_DATA_KEY]: payload[OTHER_BUSINESS_DATA_KEY]
          }),
          ...(payload?.[SELF_EMPLOYED_DATA_KEY] !== undefined && {
            [SELF_EMPLOYED_DATA_KEY]: payload[SELF_EMPLOYED_DATA_KEY]
          })
        }
      };
    },
    [UPDATE_INVOICE_FORM_ERROR]: (state, { payload }) => {
      return {
        ...state,
        invoiceFormErrors: {
          ...state.invoiceFormErrors,
          ...(payload?.[PRIVATE_PERSON_DATA_KEY] !== undefined && {
            [PRIVATE_PERSON_DATA_KEY]: payload[PRIVATE_PERSON_DATA_KEY]
          }),
          ...(payload?.[OTHER_BUSINESS_DATA_KEY] !== undefined && {
            [OTHER_BUSINESS_DATA_KEY]: payload[OTHER_BUSINESS_DATA_KEY]
          }),
          ...(payload?.[SELF_EMPLOYED_DATA_KEY] !== undefined && {
            [SELF_EMPLOYED_DATA_KEY]: payload[SELF_EMPLOYED_DATA_KEY]
          })
        }
      };
    },
    [CLEAR_INVOICES]: () => {
      return initialState;
    }
  },
  initialState
);
